import type { BillingsMonth } from '@/types'

/**
 * The billings fiscal calendar, client side.
 *
 * SEPARATE FROM `lib/fiscal.ts` ON PURPOSE. Both modules describe a March-to-February
 * year, and they label it with OPPOSED conventions: `lib/fiscal.ts` names a year by the
 * calendar year it STARTS in, and billings names it by the year it ENDS in. So the twelve
 * months from March 2026 are "FY2026" to one module and "FY2027" to the other, and a
 * function borrowed from the wrong one is off by a whole year with no error to show for
 * it. This module exists so that mistake has to be made deliberately.
 *
 * The definition here mirrors `fiscalMonths` in backend/src/services/billingsQueryService.js,
 * which is the authority. Keys must match exactly: they are the join between a target and
 * the month it applies to.
 *
 * Dates are built from calendar COMPONENTS and formatted with Intl, never parsed from a
 * string. `new Date('2026-03')` is UTC midnight and renders as February anywhere west of
 * Greenwich, which is the bug that shifted every key in the Sales Forecast overlay.
 */

const MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' })

/** The twelve months of a billings fiscal year. FY2027 is March 2026 to February 2027. */
export function billingsFiscalMonths(fiscalYear: number): BillingsMonth[] {
  return Array.from({ length: 12 }, (_, i) => {
    const m = 3 + i // March is month 1 of the fiscal year
    const year = fiscalYear - 1 + Math.floor((m - 1) / 12)
    const month = ((m - 1) % 12) + 1
    return {
      key: `${year}-${String(month).padStart(2, '0')}`,
      year,
      month,
      label: MONTH_YEAR.format(new Date(year, month - 1, 1)),
    }
  })
}

/** Which billings fiscal year a 'YYYY-MM' falls in. March onwards belongs to the next. */
export function billingsFiscalYearOf(monthKey: string): number {
  const [year, month] = monthKey.split('-').map(Number)
  return (month as number) >= 3 ? (year as number) + 1 : (year as number)
}

/** 'YYYY-MM' for today, in the browser's own calendar. */
export function currentMonthKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

/** How a period is expressed in the URL and to the API. */
export type BillingsGranularity = 'year' | 'quarter' | 'month'

/**
 * The quarters of a billings fiscal year, as slices of its months.
 *
 * DERIVED FROM `billingsFiscalMonths`, NOT CALCULATED AGAIN. That list already starts in
 * March, so quarter n is simply months (n-1)*3 to n*3, and Q4 comes out as Dec, Jan, Feb
 * without anybody having to reason about the calendar year changing inside a fiscal one.
 *
 * DO NOT USE `lib/fiscal.ts`'s quarter helpers on billings data. They describe the same
 * March-to-February year but name it by the year it STARTS in, so their '2026-Q1' is this
 * module's FY2027 Q1. Off by a year, with nothing to show for it. The whole reason this
 * module is separate is so that mistake has to be deliberate.
 */
export function billingsFiscalQuarters(fiscalYear: number) {
  const months = billingsFiscalMonths(fiscalYear)
  return [1, 2, 3, 4].map((quarter) => {
    const slice = months.slice((quarter - 1) * 3, quarter * 3)
    return {
      quarter,
      months: slice,
      // "Q3 (Sep – Nov 2025)" — the months are named because a fiscal quarter number
      // tells a reader nothing on its own when the year does not start in January.
      label: `Q${quarter} (${slice[0]?.label} – ${slice[2]?.label})`,
    }
  })
}

/** Which quarter of its fiscal year a 'YYYY-MM' falls in. */
export function billingsQuarterOf(monthKey: string): number {
  const fy = billingsFiscalYearOf(monthKey)
  const index = billingsFiscalMonths(fy).findIndex((m) => m.key === monthKey)
  return Math.floor(index / 3) + 1
}
